import React, {FC, useEffect, useMemo, useState} from 'react'
import {
  useDeleteMultiStepAutomationMutation,
  useLazyGetListMultiStepAutomationQuery,
} from '../../../../../services/cellcast.v2'
import NodataYet from '../../../../Components/NodataYet'
import {Spinner} from 'react-bootstrap'
import ReactPaginate from 'react-paginate'
import {GetListMultiStepAutomationParams} from '../../../../../services/cellcast.v2.types'
import {useLoadedData} from '../../../../hooks/useGetLoadedData'
import {AuthenticateUser} from '../../../../Utils/_helpers/RoleAndPermission'
import WriteMultiStep from './write'
import DeleteConfirmationPopup from '../../../../Components/DeleteConfirmationPopup'
import {toast} from 'react-toastify'
import {Tag} from '../../../../../services/cellcast.type'

export type MultiStepListProps = {}

const MultiStepList: FC<MultiStepListProps> = () => {
  const [params, setParams] = useState<GetListMultiStepAutomationParams>({
    page: 1,
    limit: 10,
  })
  const [isLoad, setIsLoad] = useState(true)
  const {calculate, loadedData, setLoadedData} = useLoadedData()
  const [fetchData, {isLoading, isFetching, data}] = useLazyGetListMultiStepAutomationQuery()
  const [deleteData] = useDeleteMultiStepAutomationMutation()

  const [selectedId, setSelectedId] = useState('')
  const [deleteId, setDeleteId] = useState('')
  const [showWriteModal, setShowWriteModal] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [isDeleteConfirm, setIsDeleteConfirm] = useState(false)
  const [mapTags, setMapTags] = useState<Map<string, Tag>>(new Map())

  const WriteModal = useMemo(
    () => (
      <WriteMultiStep
        show={showWriteModal}
        id={selectedId}
        closeModal={(isReload: boolean) => {
          setShowWriteModal(false)
          setSelectedId('')
          if (isReload === true) {
            fetchData(params)
          }
        }}
      />
    ),
    [showWriteModal]
  )

  const DeleteModal = useMemo(
    () => (
      <DeleteConfirmationPopup
        show={showDeleteConfirmation}
        handleClose={() => {
          setSelectedId('')
          setShowDeleteConfirmation(false)
        }}
        withInputConfirmation={false}
        Message={'Are you sure, you want to delete Automation?'}
        confirm={() => {
          setDeleteId(selectedId)
          setIsDeleteConfirm(true)
          setShowDeleteConfirmation(false)
        }}
      />
    ),
    [showDeleteConfirmation]
  )

  useEffect(() => {
    if (isLoad === true) {
      fetchData(params).finally(() => {
        setIsLoad(false)
      })
    }
  }, [isLoad])

  useEffect(() => {
    if (data?.data.tags) {
      data.data.tags.forEach((tag) => {
        setMapTags(new Map(mapTags.set(tag._id, tag)))
      })
    }
  }, [data?.data.tags])

  useEffect(() => {
    if (data) {
      const loadedData = calculate(
        data && data.data.current ? data.data.current : 1,
        params.limit,
        data && data.data.total ? data.data.total : 0
      )
      setLoadedData(loadedData)
    }
  }, [isLoading, isFetching])

  useEffect(() => {
    if (isDeleteConfirm === true && deleteId.length > 0) {
      deleteData(deleteId)
        .then((res) => {
          if ('data' in res) {
            if (res.data?.error && Object.keys(res.data?.error).length > 0) {
              toast.error(res?.data?.message)
            } else {
              toast.success(res?.data?.message)
              fetchData(params)
            }
          } else if ('error' in res) {
            toast.error(res.error)
          }
        })
        .catch((error) => {
          toast.error(`${error}`)
        })
        .finally(() => {
          setDeleteId('')
          setIsDeleteConfirm(false)
        })
    }
  }, [isDeleteConfirm])

  return (
    <div className='card mb-0 mt-8'>
      {WriteModal}
      {DeleteModal}
      <div className='card-header border-0 pt-12'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-2 mb-1'>Multi step flows</span>
          <span className='text-muted mt-1 fw-bold fs-7'>Ordered by most recent</span>
        </h3>
        <div className='card-toolbar m-0'>
          {AuthenticateUser('send-quick-sms') && (
            <div className='d-flex gap-3 align-items-center'>
              <button className='btn btn-primary' onClick={() => setShowWriteModal(true)}>
                Create New
              </button>
            </div>
          )}
        </div>
      </div>
      <div className='card-body pt-2 '>
        <div className='tab-content'>
          {isLoading ? (
            <div className='row flex-center h-200'>
              <Spinner />
            </div>
          ) : (
            <div className='card-body p-0 pt-1'>
              {data?.data.totalPages === 0 ? (
                <NodataYet message={'no multi step automation data'} />
              ) : (
                <>
                  <div className='table-responsive'>
                    <table className='table align-middle gs-0 gy-5 all-message-table'>
                      <thead>
                        <tr className='fw-bold text-muted text-uppercase'>
                          <th>RULE TITLE</th>
                          <th>RULE</th>
                          <th>SEARCH TERMS</th>
                          <th>PIPELINES</th>
                          <th>STATUS</th>
                          <th className='w-100px'>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isFetching ? (
                          <tr>
                            <td colSpan={6} className='text-center'>
                              <Spinner />
                            </td>
                          </tr>
                        ) : (
                          <>
                            {data?.data?.items?.map((item, i: number) => (
                              <React.Fragment key={i}>
                                <tr className='border-dashed border-2 border-bottom'>
                                  <td className='fw-bold cursor-pointer top-align'>
                                    {item?.title}
                                  </td>
                                  <td className='top-align'>{item?.rule}</td>
                                  <td className='top-align'>
                                    {item.rule !== 'includeTag' &&
                                    item.rule !== 'excludeTag' &&
                                    item?.searchTerm
                                      ? item?.searchTerm
                                      : ''}

                                    {(item.rule === 'includeTag' || item.rule === 'excludeTag') &&
                                    Array.isArray(item.searchTerm) ? (
                                      <div className='d-flex gap-2 align-items-center flex-wrap '>
                                        {item.searchTerm?.map((tag) => {
                                          const tagData = mapTags.get(tag)
                                          if (!tagData) {
                                            return <></>
                                          }

                                          return (
                                            <div
                                              key={i}
                                              className='inbox-tags'
                                              style={{
                                                color: tagData?.color ? 'white' : 'black',
                                                backgroundColor: tagData?.color
                                                  ? tagData?.color
                                                  : '',
                                              }}
                                            >{tagData?.name}</div>
                                          )
                                        })}
                                      </div>
                                    ) : (
                                      <></>
                                    )}
                                  </td>
                                  <td className='top-align'>{item?.pipelines?.length}</td>
                                  <td className='top-align'>
                                    <span
                                      className={`badge ${
                                        item?.status ? 'badge-light-info' : 'badge-light-danger'
                                      }`}
                                    >
                                      {item?.status ? 'Active' : 'Inactive'}
                                    </span>
                                  </td>
                                  <td className='top-align'>
                                    <div className='row'>
                                      <button
                                        className=' col-6 btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                        onClick={() => {
                                          setSelectedId(item?._id || '')
                                          setShowWriteModal(true)
                                        }}
                                        type='button'
                                      >
                                        <span className='svg-icon svg-icon-3'>
                                          <i className='bi bi-pencil-fill'></i>
                                        </span>
                                      </button>
                                      <button
                                        className=' col-6 btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                        onClick={() => {
                                          setSelectedId(item?._id || '')
                                          setShowDeleteConfirmation(true)
                                        }}
                                        type='button'
                                      >
                                        <span className='svg-icon svg-icon-3'>
                                          <i className='bi bi-trash3-fill'></i>
                                        </span>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              </React.Fragment>
                            ))}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className='abcd'>
                    <select
                      onChange={(e: any) => {
                        setParams({...params, limit: e.target.value})
                        setIsLoad(true)
                      }}
                      value={params.limit || 10}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                    {isFetching
                      ? '. . . . .'
                      : `Show ${loadedData.startLoad} - ${loadedData.endLoad} of ${
                          data?.data.total ? data.data.total : 0
                        } Messages`}
                    <ReactPaginate
                      previousLabel='<'
                      nextLabel='>'
                      pageCount={data?.data?.totalPages || 0}
                      onPageChange={(e: any) => {
                        setParams({...params, page: e.selected + 1})
                        setIsLoad(true)
                      }}
                      containerClassName={'pagination'}
                      previousLinkClassName={'pagination__link'}
                      nextLinkClassName={'pagination__link'}
                      disabledClassName={'pagination__link--disabled'}
                      activeClassName={'pagination__link--active'}
                    />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MultiStepList
